import { useUserStore } from '../../store/useUserStore';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Drawer } from '@mui/material';
import { TextField, Button } from '@mui/material';
import { COMMUNITY_USER_TYPE, COMMUNITY_QNA_TYPE } from '../../data/define';
import { useCreateQnaMutation, useEditQnaMutation } from '../../queries';
import toast from 'react-hot-toast';
import CustomSwitch from '../common/CustomSwitch';
import IcPen from '../../assets/image/ic_pen.svg';
import IcComment from '../../assets/image/community/ic_comment.svg';
import IcLike from '../../assets/image/community/ic_like.svg';
import PostDetail from '../community/qna/PostDetail';
import { useTranslation } from '../../hook/useTranslation';

function QuestionGrowth({ selectedReportId, lastReportId, questions, reload }: any) {
  const { t } = useTranslation();
  const user = useUserStore();
  const pushedRef = useRef(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [content, setContent] = useState('');
  const [editYNToggle, setEditYNToggle] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const question = questions?.find((q: any) => q.userType === COMMUNITY_USER_TYPE.USER);
  const isCreateMode = question === undefined || question === null;
  const isReadMode = question !== undefined && question !== null && !editYNToggle;
  const isEditMode = question !== undefined && question !== null && editYNToggle;

  useEffect(() => {
    if (question) {
      setIsPrivate(question.isPrivate);
      setContent(question.content);
    } else {
      setIsPrivate(false);
      setContent('');
    }
  }, [question, editYNToggle]);

  const { mutate: createQna } = useCreateQnaMutation();
  const { mutate: editQna } = useEditQnaMutation(question?.id || 0, user.id || 0);

  const handleSubmit = useCallback(() => {
    const apiParams: any = {
      content,
      questionType: COMMUNITY_QNA_TYPE.GROWTH,
      userType: COMMUNITY_USER_TYPE.USER, // Growth QNA의 경우 값 고정
      isPrivate,
      compareReportId: lastReportId,
    };
    if (isCreateMode) {
      apiParams.kidId = user?.mainKid?.id;
      apiParams.reportId = selectedReportId;
      createQna(apiParams, {
        onSuccess: () => {
          reload();
        },
        onError: (error) => {
          console.error('Error creating QnA:', error);
          toast.error(t('report.qna.toast_submit_fail'));
        },
      });
    }
    if (isEditMode) {
      apiParams.isExpertAnswer = question.isExpertAnswer;
      apiParams.isReserve = question.isReserve;
      editQna(apiParams, {
        onSuccess: () => {
          reload();
          setEditYNToggle(false);
        },
        onError: (error) => {
          console.error('Error editing QnA:', error);
          toast.error(t('report.qna.toast_submit_fail'));
        },
      });
    }
  }, [
    user,
    selectedReportId,
    lastReportId,
    isPrivate,
    content,
    isEditMode,
    isCreateMode,
    reload,
    question,
    createQna,
    editQna,
    t,
  ]);

  const openDrawer = useCallback((_id: number) => {
    setDetailOpen(true);
    window.history.pushState({ drawer: true }, '');
    pushedRef.current = true;
  }, []);

  const closeDrawer = useCallback(() => {
    setDetailOpen(false);
    if (pushedRef.current) {
      pushedRef.current = false;
      window.history.back();
    }
  }, []);

  useEffect(() => {
    const onPopState = () => {
      if (detailOpen) {
        setDetailOpen(false);
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [detailOpen]);

  return (
    <div className="w-full relative pt-[20px] pb-[20px]">
      <div className="flex items-center justify-start gap-2 mb-[20px]">
        <div className="text-[#1C1C1C] font-[700] text-[20px] text-left]">
          {t('report.growth.title')}
        </div>
      </div>
      <div className="flex justify-between mb-2 items-center gap-2">
        <div className="font-[700] text-[16px] text-[#1C1C1C] text-left">{t('report.qna.content')}</div>
        <div className="flex items-center justify-center gap-2">
          {isReadMode && (
            <button
              type="button"
              className="flex items-center justify-center gap-1 text-[14px] text-[#363636]"
              onClick={() => setEditYNToggle(true)}
            >
              {question.isPrivate ? t('report.qna.private') : ''}
              <img src={IcPen} alt="edit" />
            </button>
          )}
          {(isCreateMode || isEditMode) && (
            <CustomSwitch
              label={t('report.qna.private')}
              value={isPrivate}
              onToggle={() => setIsPrivate(!isPrivate)}
            />
          )}
        </div>
      </div>
      {isReadMode && (
        <div className="w-full relative flex flex-col justify-start items-start gap-4 mb-[12px]">
          <button
            type="button"
            className="text-[#363636] text-[14px] font-[400] w-full text-left whitespace-pre"
            onClick={() => (openDrawer as any)(question.id)}
          >
            {question?.content}
          </button>
          <div className="w-full flex justify-end items-center">
            {question?.answers?.length >= 1 && (
              <>
                <img src={IcComment} alt="chevron right" className="w-[18px] h-[18px]" />
                <span className="ml-1 text-[#969696]">{question?.answers?.length}</span>
              </>
            )}
            {question?.likeCount > 0 && (
              <>
                <img src={IcLike} alt="chevron right" className="ml-2 w-[18px] h-[18px]" />
                <span className="ml-1 text-[#969696]">{question?.likeCount}</span>
              </>
            )}
          </div>
          <Drawer
            open={detailOpen}
            onClose={closeDrawer}
            anchor="right"
            sx={{
              '& .MuiDrawer-paper': {
                width: '100vw',
                maxWidth: '100vw',
              },
            }}
          >
            <PostDetail
              drawerOpen={detailOpen}
              onClickPrev={closeDrawer}
              postId={question?.id}
              reload={reload}
            />
          </Drawer>
        </div>
      )}
      {(isCreateMode || isEditMode) && (
        <div className="w-full relative mb-[12px]">
          <div className="w-full relative">
            <TextField
              placeholder={t('report.qna.placeholder')}
              variant="outlined"
              fullWidth
              multiline
              rows={7}
              value={content}
              onChange={(e) => {
                if (e.target.value.length < 300) {
                  setContent(e.target.value);
                }
              }}
              sx={{ mb: 2 }}
            />
            <div className="absolute right-2 bottom-5 text-[12px] text-[#767676]">
              {content.length}/300
            </div>
          </div>
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            disabled={content.length < 1}
          >
            {t('report.qna.submit')}
          </Button>
        </div>
      )}
    </div>
  );
}

export default QuestionGrowth;
